/*
 * DataContext — Provider implementation
 *
 * The context definition, interface, and useDataContext hook live in
 * @repo/front-end-state so that packages/ui components can consume them.
 */
import { AppMode, Data, TSMetricData } from '@repo/frontend-types';
import { FetchResult } from '@apollo/client';
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { DateTime } from 'luxon';
import { Metric } from '@repo/backend-types';

// Hooks
import { useSafeApolloClient } from '../hooks/useApolloClient';
import { useSubscriptionHealthCheck } from '../hooks/useSubscriptionHealthCheck';
import { canServeLiveSubscription, ExtendedApolloClient } from '../utils/createGraphQLApolloClient';

// Context & Store — definition imported from front-end-state
import {
  DataContext,
  DataContextValue,
  initialDataContextState,
  useFilterContext,
  useModeContext,
  useTwinContext,
  useSettingsStore,
  useUserStore,
  useTwinConfigStore,
  createRequestSequencer,
  runLatestWins,
} from '@repo/front-end-state';

// Re-export for backwards compatibility with existing twin-viewer imports
export { DataContext, useDataContext } from '@repo/front-end-state';

// Data
import { QUERY_METRIC_VALUE } from '@repo/front-end-data';
import { getCalendarSelectionView, getBaselineSelectionView } from '@repo/front-end-services';

// Utils
import {
  buildTimeSeriesMetricList,
  buildBaselineMetricList,
  buildBaselineWeekdayWindows,
  buildNameKeyedMetricRegistry,
  getCurrentTime,
  isDatelessCalendarMode,
  describeSubscriptionError,
  mapLiveSubscriptionData,
  modeConfigFor,
  sortDataValuesByType,
  transformTimeSeriesData,
  EQUIPMENT_PHASES,
  markEnd,
  markStart,
  noteEquipmentDataArrived,
} from '@repo/front-end-utils';
interface ContextProviderProps {
  children: React.ReactNode;
  middlewareApi: string;
  metricDataWarning?: boolean;
  /**
   * When this changes, the data state returns to its initial value, the Analysis and baseline reads
   * run again and the live subscription is restarted, as on a fresh mount. Restarting it matters:
   * a running subscription delivers only on a change, so a quiet twin would sit without readings
   * until its next change, where a new one delivers the current readings at once. The protected
   * layout passes the pathname.
   */
  resetKey?: unknown;
}

export const DataContextProvider: React.FC<ContextProviderProps> = ({
  children,
  middlewareApi,
  metricDataWarning = false,
  resetKey,
}) => {
  const apolloClient = useSafeApolloClient();
  const { settings, metrics } = useSettingsStore();
  const { twin, twinBId } = useTwinContext();
  const { authMetadata } = useUserStore();
  const { heroMetrics } = useTwinConfigStore();

  /*
   * Registry for the NAME-keyed data paths (HISTORICAL + INSIGHTS): the DGraph metric list
   * unioned with the twin-config hero-metric metric names. Without this, query-service-only
   * metrics (e.g. baselineUtilisation) are absent from the DGraph-sourced `metrics` and get
   * silently dropped by transformTimeSeriesData. LIVE (id-keyed) keeps the untouched `metrics`.
   */
  const nameKeyedMetrics = useMemo(
    () => buildNameKeyedMetricRegistry(metrics, heroMetrics),
    [metrics, heroMetrics],
  );

  const {
    heroMetric,
    filter,
    startDateTime,
    finishDateTime,
    timeWindows,
    weekdayWindows,
    setLastUpdatedAt,
  } = useFilterContext();
  const { appMode } = useModeContext();
  const [dataState, setDataState] = useState<DataContextValue>(initialDataContextState);
  const timeZone = settings?.timeZone
    ? settings.timeZone
    : Intl.DateTimeFormat().resolvedOptions().timeZone;

  const prevFilterContextRef = useRef({ filter });
  const prevTwinBIdRef = useRef<string | null>(twinBId);
  // In-flight guard for the HISTORICAL time-series fetch only. (LIVE never used it; INSIGHTS now
  // uses insightsBaselineSeqRef below. A future "general cross-mode latch" — see backlog — could
  // unify these.)
  const isFetchingRef = useRef(false);
  // Bumped by a reset: a HISTORICAL read started before it neither writes nor releases the latch.
  const historicalGenerationRef = useRef(0);
  // Latest-request-wins guard for the INSIGHTS baseline fetch (insights-peak-hours-recalc).
  const insightsBaselineSeqRef = useRef(createRequestSequencer());
  // The current mode, readable by in-flight async work AFTER it has awaited. The HISTORICAL fetch
  // has no latest-wins sequencer (unlike INSIGHTS), so this is how it detects that the user left
  // the mode while its read was in flight — see the effect below for why that matters.
  const appModeRef = useRef(appMode);
  // Whether the last HISTORICAL calendar read silent-failed (and was therefore coalesced to {}),
  // so the effect can suppress the "Data last updated at …" stamp for data that never arrived.
  const historicalReadFailedRef = useRef(false);
  const lastDataReceivedRef = useRef<number | null>(null);
  const subscriptionRef = useRef<{ unsubscribe: () => void } | null>(null);
  const subscriptionCreatedAtRef = useRef<number | null>(null);

  const setData = useCallback((data: Data) => {
    setDataState(prevState => ({ ...prevState, data }));
  }, []);

  const setLiveData = useCallback((liveData: FetchResult<any> | null) => {
    setDataState(prevState =>
      /*
       * Idempotent for null→null. During an outage each retry cycle clears liveData two or three
       * times; without this, every one of them builds a new state object and a new context value and
       * re-renders every consumer — including the 3D scene — for no change at all.
       */
      prevState.liveData === null && liveData === null ? prevState : { ...prevState, liveData },
    );
    // Track when we last received data (only if it's actual data, not null)
    if (liveData !== null) {
      lastDataReceivedRef.current = Date.now();
    }
  }, []);

  /*
   * The client the ApolloProvider is CURRENTLY holding, but only if it can actually carry a
   * subscription.
   *
   * Auth metadata arrives in stages, and the zustand store learns `dgraph_namespace` synchronously
   * while the websocket-capable client is still being built asynchronously. Gating on the store's
   * namespace therefore fires during the window where the store knows the namespace but the
   * provider still holds the namespace-less (or already-stopped) client — the subscription is
   * routed to the HTTP link and errors immediately. Gating on the client's own capability removes
   * that window entirely: the effect stays idle until the client that can serve it lands.
   */
  const liveClient = canServeLiveSubscription(
    apolloClient,
    authMetadata?.orgBID,
    authMetadata?.dgraph_namespace,
  )
    ? apolloClient
    : null;

  // Use subscription health check hook to monitor and reconnect dead connections
  const {
    reconnectTrigger,
    reportSubscriptionError,
    resetSubscriptionBackoff,
    cancelPendingRetry,
  } = useSubscriptionHealthCheck({
    appMode,
    subscriptionRef,
    lastDataReceivedRef,
    subscriptionCreatedAtRef,
    setLiveData,
    // Same predicate as the subscribe effect below, deliberately: if these two could disagree,
    // the watchdogs would force reconnections the effect then declines to act on.
    isActive: !!liveClient,
  });

  const setWarning = useCallback((warning: boolean) => {
    setDataState(prevState => ({ ...prevState, warning }));
  }, []);

  /*
   * Parse the live metric data and map to ensure it's in the right shape
   * to store in the data context
   *
   */
  const parseLiveData = useCallback(
    (liveData: FetchResult<any>, metrics: Metric[]): Data => {
      const mappedData = mapLiveSubscriptionData(liveData.data.queryMetricValue, metrics);

      // Used to help catch live data issues
      const showWarning = metricDataWarning;
      if (showWarning) {
        setWarning(mappedData.warning);
      }

      // Sort data into 'buckets' of metric data
      let processedData = sortDataValuesByType(mappedData.data, metrics);

      return {
        raw: liveData.data.queryMeasure,
        processed: processedData,
      };
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- keyed on `setWarning` only; the `metricDataWarning` debug prop is not tracked
    [setWarning],
  );

  /*
   * Parse the time series metric data and map to ensure it's in the right shape
   * to store in the data context
   *
   */
  const parseTimeSeriesData = useCallback(
    (timeSeriesData: any, metrics: Metric[], stampUpdatedAt = true): Data => {
      // Map data
      let processedData = transformTimeSeriesData(timeSeriesData, metrics);

      // MainNav captions HISTORICAL with `Data last updated at <time>` whenever lastUpdatedAt is
      // set. A read that silent-failed and was coalesced to {} must not be captioned at all: an
      // empty scene captioned with the wall clock tells the user the system checked just now and
      // their site was genuinely quiet, which is a confident lie about data that was never fetched.
      // CLEARING rather than merely skipping matters — a previous successful read in this session
      // leaves a timestamp behind, and keeping it over a now-empty view claims the emptiness was
      // measured at that time. Callers that know the read failed pass false. (Before the coalesce
      // none of this could arise: the transform threw before reaching this line.)
      if (stampUpdatedAt) {
        const currentTime = getCurrentTime();
        setLastUpdatedAt(`${currentTime.substring(0, currentTime.lastIndexOf(':'))}`);
      } else {
        setLastUpdatedAt('');
      }

      return { raw: timeSeriesData, processed: processedData };
    },
    [setLastUpdatedAt],
  );

  /*
   * Fetch Time Series Data and utilise filters from filterContext
   *
   */
  const fetchTimeSeriesData = useCallback(async (): Promise<TSMetricData> => {
    let timeSeriesData: TSMetricData = {};
    let processedTimeWindows: [string, string][] = [];
    // Reset per call: only an attempted-and-failed READ suppresses the timestamp. The early returns
    // below (no historical hero metric) keep their pre-existing stamping behaviour.
    historicalReadFailedRef.current = false;

    // Use timeWindows if available, otherwise fall back to startDateTime/finishDateTime
    if (timeWindows && timeWindows.length > 0) {
      // Process multiple time windows from calendar selection
      processedTimeWindows = timeWindows.map(([start, end]) => {
        const startDateTimeQuery =
          DateTime.fromISO(start, { zone: timeZone }).toISO({
            suppressMilliseconds: true,
            includeOffset: false,
          }) || '';
        const endDateTimeQuery =
          DateTime.fromISO(end, { zone: timeZone }).toISO({
            suppressMilliseconds: true,
            includeOffset: false,
          }) || '';
        return [startDateTimeQuery, endDateTimeQuery] as [string, string];
      });
    } else {
      // Fall back to single startDateTime/finishDateTime for backwards compatibility
      if (!startDateTime) {
        throw new Error('fetchTimeSeriesData is missing value for startDateTime');
      }

      if (!finishDateTime) {
        throw new Error('fetchTimeSeriesData is missing value for finishDateTime');
      }

      const startDateTimeQuery =
        DateTime.fromISO(startDateTime, { zone: timeZone }).toISO({
          suppressMilliseconds: true,
          includeOffset: false,
        }) || '';
      const finishDateTimeQuery =
        DateTime.fromISO(finishDateTime, { zone: timeZone }).toISO({
          suppressMilliseconds: true,
          includeOffset: false,
        }) || '';

      processedTimeWindows = [[startDateTimeQuery, finishDateTimeQuery]];
    }

    if (
      authMetadata &&
      authMetadata.tokens?.accessToken &&
      authMetadata.organisation &&
      twin?.physicalModel.bID &&
      processedTimeWindows.length > 0
    ) {
      let aggregation = heroMetric?.modes?.[AppMode.HISTORICAL]?.aggregation;
      let metric = heroMetric?.valueMetricName;

      if (!aggregation || !metric) {
        // No historical-enabled hero metric is selected for this mode (e.g. every metric
        // is LIVE-only). Degrade gracefully — render no historical data instead of throwing.
        // Enablement-aware selection (FilterAndModeContexts) normally guarantees an
        // historical metric here when one exists; this is the belt-and-braces fallback.
        if (heroMetric) {
          // A metric IS selected but lacks a time-series aggregation and/or a value-metric
          // name — that is a misconfiguration (a historical-enabled metric should carry both),
          // not the legitimate "no historical metric" case. Keep it diagnosable.
          console.warn(
            '[DataContext] selected hero metric is missing its time-series aggregation or value-metric name; rendering empty historical data',
            { aggregation, valueMetricName: metric, heroMetric },
          );
        }
        return timeSeriesData;
      }

      if (!authMetadata.tsdbUrl) {
        throw new Error('Missing analytics endpoint');
      }

      // Get metrics from TwinConfig store. Fetch BOTH the value and indicator metric of
      // every hero-metric tuple (de-duplicated) — split-tuple metrics (value !== indicator)
      // otherwise lose their indicator data, and with it their badge/3D colour, in HISTORICAL.
      const metricsToFetch = buildTimeSeriesMetricList(heroMetrics);

      if (metricsToFetch.length === 0) {
        throw new Error('No hero metrics configured in TwinConfig');
      }

      // getCalendarSelectionView silent-fails to null on error — a 403 from
      // calendar-selection-view (org-membership resolution) is the case a customer actually hit.
      // Coalesce so the HISTORICAL path renders empty rather than letting null reach Object.keys in
      // transformTimeSeriesData, which throws out of this effect and leaves the viewer with nothing
      // at all. Same form as the INSIGHTS path below; keep the two in step.
      const calendarView = await getCalendarSelectionView(
        middlewareApi,
        authMetadata.tokens?.accessToken,
        authMetadata.organisation,
        twin?.physicalModel.bID,
        aggregation,
        processedTimeWindows,
        metricsToFetch,
        false,
      );
      historicalReadFailedRef.current = calendarView == null;
      timeSeriesData = calendarView ?? {};
    }

    return timeSeriesData;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- keyed on the query inputs only; `middlewareApi` is a fixed endpoint prop
  }, [
    authMetadata,
    heroMetric,
    startDateTime,
    finishDateTime,
    timeWindows,
    twin?.physicalModel.bID,
    timeZone,
    heroMetrics,
  ]);

  /*
   * Fetch the dateless baseline for INSIGHTS mode.
   *
   * Dateless analogue of fetchTimeSeriesData: instead of dated time windows it aggregates the
   * baseline ("generic week") over weekday-hour windows. With no user selection yet (q14d wires
   * that), it sends the whole-generic-week default so the asset panel / 3D populate as soon as
   * an INSIGHTS-enabled metric is configured (q14e/f/g). The response is byte-identical to the
   * calendar selection, so parseTimeSeriesData -> transformTimeSeriesData consume it unchanged.
   *
   */
  const fetchBaselineData = useCallback(async (): Promise<TSMetricData> => {
    let baselineData: TSMetricData = {};

    if (
      authMetadata &&
      authMetadata.tokens?.accessToken &&
      authMetadata.organisation &&
      twin?.physicalModel.bID
    ) {
      let aggregation = modeConfigFor(heroMetric, appMode)?.aggregation;
      let metric = heroMetric?.valueMetricName;

      if (!aggregation || !metric) {
        // No baseline-enabled hero metric is selected for this mode (e.g. no metric carries a
        // config for the current dateless mode). Degrade gracefully: render no baseline data
        // instead of throwing. Mirrors the HISTORICAL belt-and-braces fallback.
        if (heroMetric) {
          // A metric IS selected but lacks this mode's aggregation and/or value-metric name —
          // a misconfiguration (a baseline-enabled metric should carry both), not the
          // legitimate "no baseline metric" case. Keep it diagnosable.
          console.warn(
            '[DataContext] selected hero metric is missing its baseline aggregation or value-metric name; rendering empty baseline data',
            { aggregation, valueMetricName: metric, heroMetric, appMode },
          );
        }
        return baselineData;
      }

      if (!authMetadata.tsdbUrl) {
        throw new Error('Missing analytics endpoint');
      }

      // The user's calendar selection (Q14d) when narrowed, else the whole generic week (q14b
      // fallback). `null`/empty weekdayWindows means EVERYTHING selected — so the asset panel / 3D
      // still show the entity's overall baseline, and data.processed always holds the current
      // selection's aggregate (the chart/overlay read it).
      const windowsToSend =
        weekdayWindows && weekdayWindows.length > 0
          ? weekdayWindows
          : buildBaselineWeekdayWindows();

      // Fetch BOTH the value and indicator metric, de-duplicated — but ONLY for the
      // INSIGHTS-enabled hero metrics. The baseline endpoint serves only baseline metrics and
      // 400s the whole request if sent a Live/Historical-only metric (e.g. usage, countEntity from
      // other hero metrics on the same twin), so unlike HISTORICAL this must NOT be mode-agnostic.
      const metricsToFetch = buildBaselineMetricList(heroMetrics, appMode);

      if (metricsToFetch.length === 0) {
        throw new Error('No hero metrics configured in TwinConfig');
      }

      // getBaselineSelectionView silent-fails to null on error (e.g. the query-service
      // baseline endpoint is down/absent — the common case until it ships). Coalesce so the
      // INSIGHTS path renders empty rather than letting null reach Object.keys in
      // transformTimeSeriesData. (The HISTORICAL path coalesces identically in
      // fetchTimeSeriesData above — same silent-fail, same reason. Keep the two in step:
      // divergent handling of this null is how the HISTORICAL crash reached production.)
      baselineData =
        (await getBaselineSelectionView(
          middlewareApi,
          authMetadata.tokens?.accessToken,
          authMetadata.organisation,
          twin?.physicalModel.bID,
          aggregation,
          windowsToSend,
          metricsToFetch,
          false,
        )) ?? {};
    }

    return baselineData;
  }, [
    authMetadata,
    heroMetric,
    twin?.physicalModel.bID,
    heroMetrics,
    middlewareApi,
    weekdayWindows,
    appMode,
  ]);

  // Keep appModeRef in step with the rendered mode, for the in-flight staleness check below.
  //
  // useLayoutEffect, NOT useEffect, and this is load-bearing: passive effects are flushed in a
  // scheduler macrotask after commit, whereas the awaited fetch resumes in a MICROTASK. So with a
  // passive effect there is a window — mode change committed, ref not yet updated — in which an
  // in-flight read that resolves right then still sees the old mode, and the staleness guard below
  // waves through exactly the write it exists to stop. Layout effects run synchronously during
  // commit, before any microtask checkpoint that follows it, which closes that window. Assigning
  // during render would also be synchronous but is wrong under concurrent rendering, where a
  // discarded render would leave the ref holding a value that never committed.
  useLayoutEffect(() => {
    appModeRef.current = appMode;
  }, [appMode]);

  // Declared before the fetch effects below, so on a reset their re-run writes land after it. The
  // backoff is zeroed too, as a fresh mount's would be, so the restarted subscription's first
  // failure retries from 5s rather than carrying the old feed's growing delay. A HISTORICAL read
  // still in flight is abandoned, as a remount abandoned it: the re-run reads afresh.
  const resetKeyRef = useRef(resetKey);
  useEffect(() => {
    if (resetKeyRef.current === resetKey) return;
    resetKeyRef.current = resetKey;
    setDataState(initialDataContextState);
    resetSubscriptionBackoff();
    historicalGenerationRef.current += 1;
    isFetchingRef.current = false;
  }, [resetKey, resetSubscriptionBackoff]);

  /*
   * For Live Mode
   *
   */
  useEffect(() => {
    const getLiveData = async () => {
      let data;
      // OPERATIONS parses the live dGraph subscription exactly like LIVE (replace-embeddable R1) —
      // its headline occupancy KPI reads processed['countEntity'] from this data.
      if (appMode === AppMode.LIVE || appMode === AppMode.OPERATIONS) {
        if (dataState.liveData) {
          data = parseLiveData(dataState.liveData, metrics);
          setData(data);
        }
      }
    };

    // Ensure the metrics data has been fetched
    if (metrics && metrics.length > 0) {
      getLiveData();
    }

    prevFilterContextRef.current.filter = filter;
  }, [dataState.liveData, filter, metrics, appMode, parseLiveData, setData]);

  /*
   * For Time Series (offline) Mode
   *
   */
  useEffect(() => {
    const getTimeSeriesData = async () => {
      if (isFetchingRef.current) return; // Prevent duplicate requests

      let data;
      if (appMode === AppMode.HISTORICAL) {
        const generation = historicalGenerationRef.current;
        try {
          isFetchingRef.current = true;
          const timeSeriesdata = await fetchTimeSeriesData();
          // A reset since this read began: its re-run owns the data now.
          if (generation !== historicalGenerationRef.current) return;
          // Drop a stale result rather than writing it. These reads are second-scale, so the user
          // can easily leave HISTORICAL while one is in flight; writing then would clobber the mode
          // they are NOW in, and INSIGHTS/EQUIPMENT do not refetch on their own, so their panel /
          // chart / 3D would sit empty until the next interaction. INSIGHTS is already protected
          // this way by its sequencer; this is the HISTORICAL equivalent. Load-bearing since the
          // null coalesce: a failed read used to throw here and never reach setData at all, so
          // coalescing to {} is what made this write reachable.
          if (appModeRef.current !== AppMode.HISTORICAL) return;
          data = parseTimeSeriesData(
            timeSeriesdata,
            nameKeyedMetrics,
            !historicalReadFailedRef.current,
          );
          setData(data);
        } finally {
          if (generation === historicalGenerationRef.current) isFetchingRef.current = false;
        }
      }
    };

    // Ensure the metrics data has been fetched
    if (metrics && metrics.length > 0) {
      getTimeSeriesData();
    }
  }, [
    filter,
    appMode,
    metrics,
    nameKeyedMetrics,
    fetchTimeSeriesData,
    parseTimeSeriesData,
    setData,
    resetKey,
  ]);

  /*
   * For dateless baseline modes (INSIGHTS + EQUIPMENT)
   *
   */
  useEffect(() => {
    if (!isDatelessCalendarMode(appMode)) return;
    // Ensure the metrics data has been fetched
    if (!(metrics && metrics.length > 0)) return;

    // Latest-request-wins (insights-peak-hours-recalc): unlike LIVE/HISTORICAL, the INSIGHTS
    // baseline fetch must NOT drop a refetch that is requested while one is in flight. Entering
    // INSIGHTS starts the whole-week fetch; a Peak-Hours toggle (or any selection change) then
    // commits new `weekdayWindows` while that first fetch is still running. The old shared
    // `isFetchingRef` early-return silently dropped that second fetch, so `data.processed` stayed
    // on the whole-week aggregate and the Total chart + selection overlay never recalculated to the
    // peak hours. `runLatestWins` issues every request and applies only the latest, so the newest
    // selection wins.
    // `equipment:fetch:baseline` — the SECOND of Equipment mode's two independent fetches (the
    // first, the classifications read, is timed in TwinPage). Only marked in EQUIPMENT: this effect
    // also serves INSIGHTS, and attributing an Insights fetch to an equipment phase would corrupt
    // the numbers the investigation rests on.
    const isEquipmentLoad = appMode === AppMode.EQUIPMENT;
    if (isEquipmentLoad) markStart(EQUIPMENT_PHASES.fetchBaseline);

    runLatestWins(insightsBaselineSeqRef.current, fetchBaselineData, baselineData => {
      if (isEquipmentLoad) markEnd(EQUIPMENT_PHASES.fetchBaseline);
      setData(parseTimeSeriesData(baselineData, nameKeyedMetrics));
      // The baseline VALUES are now in the store. Together with the classifications this opens the two
      // render phases (R2's "data in store"). Idempotent per load, so a filter-driven refetch does not
      // restart them.
      if (isEquipmentLoad) noteEquipmentDataArrived('baseline');
    });

    // Invalidate an in-flight request when the user leaves INSIGHTS, the effect re-runs, or the
    // provider unmounts: bumping the sequencer makes the awaited result fail its `isLatest` check
    // (so it is dropped instead of clobbering `data.processed` / setting state after unmount).
    return () => {
      // eslint-disable-next-line react-hooks/exhaustive-deps -- the ref holds one sequencer for the provider's life; it is never reassigned
      insightsBaselineSeqRef.current.next();
    };
  }, [
    filter,
    appMode,
    metrics,
    nameKeyedMetrics,
    fetchBaselineData,
    parseTimeSeriesData,
    setData,
    resetKey,
  ]);

  /*
   * Clear liveData when twin changes to allow fresh connection
   */
  useEffect(() => {
    if (prevTwinBIdRef.current !== twinBId) {
      // Twin has changed, clear liveData to allow fresh connection
      if (prevTwinBIdRef.current !== null) {
        console.debug('DataContext: Twin changed, clearing liveData to restore connection');
        setLiveData(null);
        lastDataReceivedRef.current = null;
      }
      prevTwinBIdRef.current = twinBId;
    }
  }, [twinBId, setLiveData]);

  /*
   * LIVE Subscription to GraphQL Headcount Measure Data
   * This continually runs in the background regardless of the Live/Time series state
   * Only subscribes if the organization has a dgraph namespace
   *
   */
  useEffect(() => {
    if (liveClient) {
      // Clear liveData before creating new subscription to ensure fresh connection
      setLiveData(null);
      lastDataReceivedRef.current = null;

      // Clean up any existing subscription before creating a new one
      if (subscriptionRef.current) {
        try {
          subscriptionRef.current.unsubscribe();
        } catch (error) {
          console.warn('Error unsubscribing existing subscription:', error);
        }
        subscriptionRef.current = null;
      }

      // Set by the error callback, so the assignment after subscribe cannot resurrect a dead ref.
      let errored = false;

      const subscribeToLiveData = () => {
        const subscription = liveClient.subscribe({ query: QUERY_METRIC_VALUE }).subscribe({
          next(liveData) {
            // Data arriving is what proves the feed healthy, so it is what clears the backoff.
            resetSubscriptionBackoff();
            setLiveData(liveData);
          },
          error(error) {
            errored = true;
            /*
             * The described string first, then the raw object. `console.error('Subscription error:',
             * error)` alone renders as the literal "Subscription error: Object" in a production
             * console, which is why a real customer-reported outage could not be diagnosed from the
             * report. The namespace is in the prefix because the message alone cannot say WHICH
             * org's feed died. The raw object is kept alongside — it is the machine-readable payload
             * a support engineer expands in devtools — but `graphQLErrors`/`networkError` are not
             * repeated as separate keys, since they already live on it and in the string.
             */
            console.error(
              `Subscription error [namespace ${liveClient._dgraphNamespace}]:`,
              describeSubscriptionError(error),
              error,
            );
            // Clear liveData on error to allow retry when subscription is re-established
            setLiveData(null);
            lastDataReceivedRef.current = null;
            subscriptionRef.current = null;
            // ...and actually retry. Clearing state used to be all this did, which left the tab
            // with no live data until the user reloaded.
            reportSubscriptionError();
          },
        });

        // Return the cleanup function
        return subscription;
      };

      // Store the subscription reference and track when it was created
      const subscription = subscribeToLiveData();
      /*
       * Only if the error handler has not already fired. An error delivered synchronously during
       * `.subscribe()` nulls the ref, and reassigning it here would resurrect a reference to a dead
       * subscription — which makes both watchdogs take the "exists but silent" path and wait five
       * minutes, instead of the null-ref backstop that was added for exactly this case.
       */
      if (!errored) {
        subscriptionRef.current = subscription;
        subscriptionCreatedAtRef.current = Date.now();
        /*
         * A subscription now exists, so any retry scheduled for its ABSENCE is moot. Without this,
         * a retry armed against the previous client fires seconds later and tears down a perfectly
         * healthy subscription. The spec requires the backoff be "cancelled on effect cleanup
         * (client change, unmount)" and only the unmount half was implemented. Deliberately NOT
         * `resetSubscriptionBackoff()`: that would zero the attempt counter on every reconnect and
         * flatten the exponential schedule to a permanent 5s.
         */
        cancelPendingRetry();
      }

      // Clean up when component unmounts or when apolloClient changes
      return () => {
        if (subscription) {
          console.debug(
            'DataContext: Cleaning up metric value subscription (normal behavior when changing organizations or unmounting)',
          );
          subscription.unsubscribe();
        }
        subscriptionRef.current = null;
        lastDataReceivedRef.current = null;
        subscriptionCreatedAtRef.current = null;
      };
    } else if (apolloClient) {
      /*
       * The client the provider holds cannot serve THIS org's live feed. Three distinct cases, and
       * they have to be told apart — an earlier shape collapsed them and left one of the three
       * silently terminal, which is the failure mode this whole spec exists to remove.
       */
      const held = apolloClient as ExtendedApolloClient;
      if (!authMetadata?.dgraph_namespace) {
        // This org has no namespace. There will never be a live feed, and nothing is wrong.
        console.warn('DataContext: No dgraph namespace available, skipping live data subscription');
      } else if (held._dgraphNamespace && !held._hasWebSocketLink) {
        /*
         * The client was BUILT for a namespace and still has no websocket link, so the factory's
         * wsLink construction failed. Nothing rebuilds it — ProtectedGate's effect depends only on
         * the access token, orgBID and namespace, none of which will change again — so this session
         * will never get live data. Logged loudly because the alternative is exactly the silent,
         * permanent blankness a customer reported and nobody could diagnose.
         */
        console.error(
          `DataContext: the Apollo client for namespace ${held._dgraphNamespace} (${held._wsUrl}) has no websocket link, so live data cannot arrive and will not be retried`,
        );
      }
      /*
       * The third case needs no log: the client that CAN serve this org simply has not landed yet —
       * the boot race, or the window during an org switch. The effect runs again the moment it does,
       * and warning here would be both wrong and the loudest line in a healthy boot log. State is
       * still cleared in all three cases, though: otherwise the PREVIOUS org's readings sit in
       * context, presented as this org's, for the whole swap window.
       */
      setLiveData(null);
      lastDataReceivedRef.current = null;
      subscriptionRef.current = null;
      subscriptionCreatedAtRef.current = null;
    }
    /*
     * Every dependency here except `resetKey` is stable by construction — `setLiveData` is a
     * `useCallback([])`, and `reportSubscriptionError`/`resetSubscriptionBackoff` deliberately read
     * appMode and isActive through a ref inside the hook for exactly this reason. If any of them
     * ever gained a changing identity, this effect would tear down and recreate the live websocket
     * on every render that changed it — see the mode-switch pin in
     * DataContext.liveSubscription.test.tsx. `resetKey` changes on purpose: a reset restarts the
     * subscription as a remount did, so a quiet twin gets its current readings at once (see the
     * prop's own comment). Do not drop it to restore stability.
     */
  }, [
    liveClient,
    apolloClient,
    authMetadata?.dgraph_namespace,
    setLiveData,
    reconnectTrigger,
    reportSubscriptionError,
    resetSubscriptionBackoff,
    cancelPendingRetry,
    resetKey,
  ]);

  const contextValue = useMemo(() => ({ ...dataState }), [dataState]);

  return <DataContext.Provider value={contextValue}>{children}</DataContext.Provider>;
};
